import { Head, Link, router } from '@inertiajs/react'
import { Pencil, Trash2 } from 'lucide-react'
import type { CompanyDashboardProps } from '@/types/company'
import { CompanyHeader } from '@/components/Shared/CompanyHeader'
import { CompanyWorkspaceNav } from '@/components/Shared/CompanyWorkspaceNav'
import { ConfirmDeleteDialog } from '@/components/Shared/ConfirmDeleteDialog'

function companyInitials(name: string, domain: string): string {
  const letters = name
    .split(/\s+/)
    .filter(Boolean)
    .map((word) => word[0])
    .join('')
    .slice(0, 2)
    .toUpperCase()

  return letters || domain[0]?.toUpperCase() || ''
}

export default function CompanyDashboard({
  company,
  teams,
  positions,
  funnelSummary,
  workspaceSetup,
}: CompanyDashboardProps) {
  const initials = companyInitials(company.name, company.domain)
  const setupIncomplete =
    !workspaceSetup.profileConfirmed ||
    workspaceSetup.positionsCount === 0

  return (
    <>
      <Head title="Dashboard — Hyrin" />

      <div className="min-h-screen bg-muted">
        <CompanyHeader />

        <main className="mx-auto max-w-[1120px] px-6 py-[52px]">
          <p className="mb-[14px] font-mono text-[11px] uppercase tracking-eyebrow text-primary">
            // company workspace
          </p>

          <div className="flex items-center gap-4">
            <div className="flex size-[44px] shrink-0 items-center justify-center rounded-full bg-primary font-heading text-[16px] font-bold text-primary-foreground">
              {initials}
            </div>
            <div>
              <h1 className="font-heading text-[36px] font-bold leading-[1.06] tracking-[-0.025em] text-foreground">
                {company.name}
              </h1>
              <p className="mt-1 font-mono text-[12px] text-muted-foreground">
                {company.domain}
                {company.website && (
                  <>
                    {' · '}
                    <a
                      href={company.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary hover:underline"
                    >
                      {company.website}
                    </a>
                  </>
                )}
              </p>
            </div>
            <Link
              href="/company/profile/edit"
              className="ml-auto font-mono text-[12px] text-primary"
            >
              Edit profile →
            </Link>
          </div>

          <div className="mt-7">
            <CompanyWorkspaceNav active="home" />
          </div>

          {setupIncomplete && (
            <div className="mt-5 rounded-card border border-dashed border-border p-[18px]">
              <span className="font-sans text-[13px] text-muted-foreground">
                Finish setting up your workspace
              </span>
              <Link
                href="/company/profile/edit"
                className="ml-3 font-mono text-[12px] text-primary"
              >
                Continue setup →
              </Link>
            </div>
          )}

          <div className="mt-8 flex flex-col gap-6 rounded-card bg-primary p-8 text-primary-foreground shadow-[0_4px_40px_rgba(20,40,32,0.08)] sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="font-mono text-[11px] uppercase tracking-eyebrow text-primary-foreground/70">
                // hyrin funnel
              </p>
              <p className="mt-3 flex items-baseline gap-3">
                <span className="font-heading text-[42px] font-bold leading-none">
                  {funnelSummary.totalCandidates}
                </span>
                <span className="font-sans text-[16px] text-primary-foreground/85">
                  {funnelSummary.totalCandidates === 1 ? 'candidate' : 'candidates'} in your
                  pipeline
                </span>
              </p>
              <p className="mt-[10px] font-mono text-[12px] text-primary-foreground/70">
                across {positions.length} {positions.length === 1 ? 'open role' : 'open roles'}
              </p>
            </div>
            <Link
              href="/company/funnel"
              className="inline-flex h-[52px] shrink-0 items-center rounded-cta bg-background px-6 font-sans text-[16px] font-semibold text-foreground transition-opacity hover:opacity-90"
            >
              Open the Hyrin funnel →
            </Link>
          </div>

          <div className="mt-8 flex gap-8">
            <div>
              <p className="font-heading text-[28px] font-bold text-foreground">{teams.length}</p>
              <p className="mt-[3px] font-mono text-[11px] text-muted-foreground">Teams</p>
            </div>
            <div className="border-l border-border pl-8">
              <p className="font-heading text-[28px] font-bold text-foreground">
                {positions.length}
              </p>
              <p className="mt-[3px] font-mono text-[11px] text-muted-foreground">
                Open positions
              </p>
            </div>
            <div className="border-l border-border pl-8">
              <p className="font-heading text-[28px] font-bold text-foreground">
                {funnelSummary.totalCandidates}
              </p>
              <p className="mt-[3px] font-mono text-[11px] text-muted-foreground">In pipeline</p>
            </div>
          </div>

          <div className="mt-8 grid gap-6 md:grid-cols-2">
            <div className="rounded-card bg-background p-8 shadow-sm">
              <div className="mb-5 flex items-center justify-between">
                <span className="font-mono text-[11px] uppercase tracking-[0.08em] text-muted-foreground">
                  {teams.length} {teams.length === 1 ? 'team' : 'teams'}
                </span>
                <Link
                  href="/company/positions"
                  className="font-sans text-[13px] text-primary hover:underline"
                >
                  Manage →
                </Link>
              </div>

              {teams.length === 0 ? (
                <p className="font-sans text-[14px] text-muted-foreground">
                  No teams yet.{' '}
                  <Link href="/company/positions" className="text-primary hover:underline">
                    Add a position and attach a team
                  </Link>
                  .
                </p>
              ) : (
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="pb-3 text-left font-mono text-[11px] font-medium uppercase tracking-eyebrow text-muted-foreground">
                        Team
                      </th>
                      <th className="pb-3 text-right font-mono text-[11px] font-medium uppercase tracking-eyebrow text-muted-foreground">
                        Open positions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {teams.map((team) => (
                      <tr key={team.id}>
                        <td className="py-3 font-sans text-[14px] font-medium text-foreground">
                          <Link
                            href={`/company/funnel?team_id=${team.id}`}
                            className="text-foreground hover:text-primary"
                          >
                            {team.name}
                          </Link>
                        </td>
                        <td className="py-3 text-right font-mono text-[13px] text-muted-foreground">
                          {team.positionsCount}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            <div className="rounded-card bg-background p-8 shadow-sm">
              <div className="mb-5 flex items-center justify-between">
                <span className="font-mono text-[11px] uppercase tracking-[0.08em] text-muted-foreground">
                  {positions.length} {positions.length === 1 ? 'position' : 'positions'}
                </span>
                <Link
                  href="/company/positions"
                  className="font-sans text-[13px] text-primary hover:underline"
                >
                  Manage →
                </Link>
              </div>

              {positions.length === 0 ? (
                <p className="font-sans text-[14px] text-muted-foreground">
                  No positions yet.{' '}
                  <Link href="/company/positions" className="text-primary hover:underline">
                    Create your first position
                  </Link>
                  .
                </p>
              ) : (
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="pb-3 text-left font-mono text-[11px] font-medium uppercase tracking-eyebrow text-muted-foreground">
                        Position
                      </th>
                      <th className="pb-3 text-left font-mono text-[11px] font-medium uppercase tracking-eyebrow text-muted-foreground">
                        Location
                      </th>
                      <th className="pb-3 text-left font-mono text-[11px] font-medium uppercase tracking-eyebrow text-muted-foreground">
                        Team
                      </th>
                      <th className="pb-3 text-right font-mono text-[11px] font-medium uppercase tracking-eyebrow text-muted-foreground">
                        <span className="sr-only">Actions</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {positions.map((position) => (
                      <tr key={position.id}>
                        <td className="py-3 font-sans text-[14px] font-medium">
                          <Link
                            href={`/company/funnel?position_id=${position.id}`}
                            className="text-foreground hover:text-primary"
                          >
                            {position.title}
                          </Link>
                        </td>
                        <td className="py-3 font-sans text-[14px] text-muted-foreground">
                          {position.location}
                        </td>
                        <td className="py-3 font-sans text-[14px]">
                          {position.team ? (
                            <Link
                              href={`/company/funnel?team_id=${position.team.id}`}
                              className="text-foreground hover:text-primary"
                            >
                              {position.team.name}
                            </Link>
                          ) : (
                            <span className="text-muted-foreground">Company-wide</span>
                          )}
                        </td>
                        <td className="py-3 text-right">
                          <span className="inline-flex items-center gap-3">
                            <Link
                              href={`/company/positions/${position.id}`}
                              aria-label="Edit position"
                              className="text-muted-foreground hover:text-foreground"
                            >
                              <Pencil className="size-[16px]" />
                            </Link>
                            <ConfirmDeleteDialog
                              trigger={
                                <button
                                  type="button"
                                  aria-label="Delete position"
                                  className="text-muted-foreground hover:text-destructive"
                                >
                                  <Trash2 className="size-[16px]" />
                                </button>
                              }
                              title="Delete position?"
                              description={`This removes “${position.title}” and its pipeline candidates. This can't be undone.`}
                              onConfirm={() => router.delete(`/company/positions/${position.id}`)}
                            />
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </main>
      </div>
    </>
  )
}
